/**
 * 클라이언트 JWT 유틸리티
 * - 서명 검증은 서버(server/src/utils/jwt.ts)의 책임. 여기서는 만료(exp) 여부만 확인한다.
 * - 별도 패키지(jwt-decode 등) 설치 없이 atob + JSON.parse 로 payload만 디코딩한다.
 */

interface JwtPayloadWithExp {
  exp?: number;
  [key: string]: unknown;
}

/** 클라이언트-서버 시계 오차 허용치 (초) */
const CLOCK_SKEW_LEEWAY_SECONDS = 30;

/**
 * JWT의 payload(두 번째 세그먼트)를 디코딩한다.
 * 형식이 올바르지 않으면 null을 반환한다 (호출부에서 만료로 간주해 안전하게 처리).
 */
export function decodeJwtPayload(token: string): JwtPayloadWithExp | null {
  try {
    const [, payloadSegment] = token.split('.');
    if (!payloadSegment) return null;

    // base64url → base64 변환 후 디코딩
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((char) => '%' + char.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );

    return JSON.parse(json) as JwtPayloadWithExp;
  } catch {
    return null;
  }
}

/**
 * 토큰이 만료되었는지 확인한다.
 * - 토큰이 없거나, 디코딩 실패, exp 필드가 없는 경우 → 만료된 것으로 간주 (안전한 기본값)
 * - 시계 오차 대비, 실제 만료 leewaySeconds 전부터 미리 만료된 것으로 간주한다(안전 마진).
 */
export function isTokenExpired(
  token: string | null | undefined,
  leewaySeconds: number = CLOCK_SKEW_LEEWAY_SECONDS
): boolean {
  if (!token) return true;

  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return true;

  const nowInSeconds = Date.now() / 1000;
  return payload.exp - leewaySeconds < nowInSeconds;
}
